// Lifecycle-stage derivation for the AwardArc on the brief detail page.
//
// Extracted from the route module so tests can import it without React deps.
//
// Stages (numeric indices match AwardArc's STAGES[]):
//   0 RFI · 1 SOURCES SOUGHT · 2 DRAFT RFP · 3 RFP · 4 Q&A · 5 PROPOSAL ·
//   6 EVAL · 7 AWARD · 8 PERFORMANCE
//
// Plus the sentinel STAGE_UNKNOWN (-1) used when the inputs don't justify
// claiming any specific lifecycle position. AwardArc renders the whole 9-stage
// track as faded when stage === STAGE_UNKNOWN.
//
// Why a sentinel rather than a 10th band: the lifecycle is an *ordering*
// claim. UNKNOWN is the *absence* of an ordering claim, not a new position in
// the sequence. Adding a 10th dot would imply UNKNOWN comes after PERFORMANCE,
// which is the opposite of what we mean.

export const STAGE_UNKNOWN = -1;

export interface LifecycleInputs {
  status: string;
  notice_type: string | null;
  closing_date: string | null;
  amendment_date: string | null;
  amendment_number: string | null;
  expected_start_date: string | null;
  expected_end_date: string | null;
}

export interface LifecycleAward {
  start_date: string | null;
  end_date: string | null;
  status?: string;
}

// Bug context: Sabre dataset is 99.8% status='expired' (historical scraped
// rows, closing_date usually >180d ago). The previous implementation collapsed
// every 'expired' row to EVAL (stage 6) — Youssef saw "every contract is EVAL"
// because functionally every contract had status=expired and the function
// trusted that as evidence of evaluation in progress.
//
// New contract:
//   - status='cancelled' or 'expired' is NOT, by itself, evidence of EVAL.
//   - EVAL only fires when a recent (within 180d) past-closing on an active
//     row makes evaluation plausible, OR an explicitly cancelled status (which
//     is a real terminal state distinct from "expired = the listing aged out").
//   - When the necessary inputs are absent for any meaningful claim,
//     return STAGE_UNKNOWN instead of silently defaulting to RFP/EVAL.
export function deriveLifecycleStage(
  o: LifecycleInputs,
  awards: LifecycleAward[],
  now: number = Date.now(),
): number {
  const status = (o.status ?? '').toLowerCase();
  const today = now;
  const days = o.closing_date
    ? Math.round((new Date(o.closing_date).getTime() - today) / 86_400_000)
    : null;

  // Period-of-performance window. Prefer the linked award's dates (more
  // accurate post-award), fall back to the tender's expected_start/end.
  const award = awards[0];
  const startStr = award?.start_date ?? o.expected_start_date;
  const endStr = award?.end_date ?? o.expected_end_date;
  const startMs = startStr ? new Date(startStr).getTime() : null;
  const endMs = endStr ? new Date(endStr).getTime() : null;
  const inPerformance =
    (startMs != null && startMs <= today && (endMs == null || endMs >= today)) ||
    (startMs != null && endMs != null && endMs < today);

  // 8 PERFORMANCE — award exists AND we're inside (or past) the PoP.
  if (awards.length > 0 && inPerformance) return 8;
  if (status === 'awarded' && inPerformance) return 8;

  // 7 AWARD — a linked award row, or status === "awarded", with PoP not yet
  // started (or no PoP dates known).
  if (awards.length > 0) return 7;
  if (status === 'awarded') return 7;

  // 6 EVAL — only when there's real evidence evaluation is in progress.
  //   - status='cancelled' is a real terminal decision the agency made → EVAL
  //     here represents "the evaluation outcome was cancellation". Distinct
  //     from 'expired' which just means "the listing aged out".
  //   - status='active' and closing within the last 180d: evaluation is
  //     plausibly underway.
  if (status === 'cancelled') return 6;
  if (status === 'active' && days != null && days < 0 && days > -180) return 6;

  const nt = (o.notice_type ?? '').toLowerCase();
  const hasNoticeType = nt.length > 0;
  const hasAmendment =
    !!o.amendment_date ||
    (o.amendment_number != null &&
      o.amendment_number !== '000' &&
      o.amendment_number.trim() !== '');

  // Notice-type early stages take priority over amendment/proposal heuristics —
  // an RFI that happens to be amended is still an RFI, not Q&A.
  // 0 RFI: Request for Information / Demande de renseignements / Letter of Interest
  if (/request for information|demande de renseignements|letter of interest|\brfi\b/.test(nt))
    return 0;
  // 1 SOURCES SOUGHT: rare in CA; pattern match for forward compat with SAM.gov
  if (/sources sought|notice of intent|appel à manifestation/.test(nt)) return 1;
  // 2 DRAFT RFP: ACAN ≈ Canadian draft-RFP signal (intent before final solicitation)
  if (
    /advance contract award notice|préavis d'adjudication|préavis d.adjudication|\bacan\b|draft rfp/.test(
      nt,
    )
  )
    return 2;

  // 4 Q&A — amendments mean clarifications issued. Only credit Q&A if proposal
  // isn't due imminently — inside the 14-day window the PROPOSAL phase
  // dominates regardless of past amendments.
  if (hasAmendment && (days == null || days >= 14)) return 4;

  // 5 PROPOSAL — closing within 14 days, no other earlier-stage signal.
  if (days != null && days >= 0 && days < 14) return 5;

  // 3 RFP — active solicitations with a recognized RFP-family notice type or
  // currently-open closing window. Recognizing the notice_type is what makes
  // this an "RFP" claim rather than a guess.
  const isRfpFamily =
    /request for proposal|request for standing offer|request for supply arrangement|invitation to qualify|rfp against supply arrangement|appel d'offres|appel d.offres|invitation à soumissionner/.test(
      nt,
    );
  if (status === 'active' && isRfpFamily) return 3;
  // Active row with a future closing date and any notice_type — still an RFP
  // by behavior even if we don't recognize the exact label.
  if (status === 'active' && hasNoticeType && days != null && days >= 0) return 3;

  // Everything below is the "we don't have enough to claim a stage" path.
  //
  // status='expired' WITHOUT a recognized notice-type, without a linked award,
  // without recent closing, without amendment dates, without PoP dates is the
  // exact shape of the Sabre dataset — historical rows where the source
  // simply marked them expired. Refusing to pretend we know the lifecycle for
  // these is the whole point of this change.
  if (status === 'expired' && !hasNoticeType) return STAGE_UNKNOWN;
  if (status === 'expired' && !isRfpFamily && !hasAmendment) return STAGE_UNKNOWN;

  // status='expired' WITH an RFP-family notice type and no other signal: we
  // can at least say it's an RFP-shaped record. Still RFP, not EVAL.
  if (status === 'expired' && isRfpFamily) return 3;

  // Unknown status, missing essentially everything → UNKNOWN.
  if (!hasNoticeType && status !== 'active') return STAGE_UNKNOWN;

  // Active solicitation with no other signal → default RFP.
  return 3;
}
